import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  LayoutDashboard, 
  FileText, 
  Euro, 
  Mail, 
  LogOut, 
  Save,
  Trash2,
  Plus,
  MessageCircle,
  BarChart3,
  Send,
  Users
} from 'lucide-react';
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('content');
  const [loading, setLoading] = useState(false);
  
  // États pour le contenu
  const [availabilityContent, setAvailabilityContent] = useState({
    title: '',
    content: ''
  });
  const [availAvailableEdit, setAvailAvailableEdit] = useState({ title: '', content: '' });
  const [availBookedEdit, setAvailBookedEdit] = useState({ title: '', content: '' });
  
  // États pour les tarifs
  const [pricing, setPricing] = useState([]);
  const [weeklyPricing, setWeeklyPricing] = useState([]);
  const [savingWeekId, setSavingWeekId] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);
  
  // États pour les messages
  const [messages, setMessages] = useState([]);

  // États pour le chatbot / statistiques
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [sendingReport, setSendingReport] = useState(false);
  
  useEffect(() => {
    checkAuth();
    loadData();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    try {
      await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadData = async () => {
    try {
      // Charger le contenu
      const contentRes = await axios.get(`${API}/admin/content/availability_notice`, {
        headers: getAuthHeaders()
      });
      if (contentRes.data) {
        setAvailabilityContent({
          title: contentRes.data.title || '',
          content: contentRes.data.content || ''
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erreur chargement contenu:', error);
      }
    }

    // Charger les deux pavés de disponibilité (page Réservation)
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/content/availability_available`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/content/availability_booked`, { headers: getAuthHeaders() }),
      ]);
      if (a.data) setAvailAvailableEdit({ title: a.data.title || '', content: a.data.content || '' });
      if (b.data) setAvailBookedEdit({ title: b.data.title || '', content: b.data.content || '' });
    } catch (error) {
      if (error.response?.status !== 404) console.error('Erreur chargement pavés:', error);
    }

    try {
      // Charger les tarifs
      const pricingRes = await axios.get(`${API}/admin/pricing`, {
        headers: getAuthHeaders()
      });
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Erreur chargement tarifs:', error);
    }

    try {
      // Charger les messages
      const messagesRes = await axios.get(`${API}/admin/messages`, {
        headers: getAuthHeaders()
      });
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
    }
  };

  const handleSaveContent = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/content/availability_notice`,
        availabilityContent,
        { headers: getAuthHeaders() }
      );
      
      toast({
        title: "Succès",
        description: "Le contenu a été mis à jour",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les modifications",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBlock = async (section, data) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/content/${section}`, data, { headers: getAuthHeaders() });
      toast({ title: "Succès", description: "Pavé de disponibilité mis à jour" });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder ce pavé", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePricing = async (pricingItem) => {
    try {
      await axios.put(
        `${API}/admin/pricing/${pricingItem.id}`,
        {
          period: pricingItem.period,
          price: pricingItem.price,
          description: pricingItem.description,
          order: pricingItem.order
        },
        { headers: getAuthHeaders() }
      );
      
      toast({
        title: "Succès",
        description: "Le tarif a été mis à jour",
      });
      
      setEditingPricing(null);
      loadData();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le tarif",
        variant: "destructive"
      });
    }
  };

  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) return;
    
    try {
      await axios.delete(`${API}/admin/pricing/${pricingId}`, {
        headers: getAuthHeaders()
      });
      
      toast({
        title: "Succès",
        description: "Le tarif a été supprimé",
      });
      
      loadData();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le tarif",
        variant: "destructive"
      });
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await axios.put(
        `${API}/admin/messages/${messageId}/read`,
        {},
        { headers: getAuthHeaders() }
      );
      loadData();
    } catch (error) {
      console.error('Erreur marquage message:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) return;
    
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`, {
        headers: getAuthHeaders()
      });
      
      toast({
        title: "Succès",
        description: "Le message a été supprimé",
      });
      
      loadData();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le message",
        variant: "destructive"
      });
    }
  };

  const loadConversations = async () => {
    try {
      const res = await axios.get(`${API}/admin/conversations`, { headers: getAuthHeaders() });
      setConversations(res.data || []);
    } catch (e) { console.error('Erreur chargement admin:', e); }
  };

  const loadConversationDetail = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/admin/conversations/${sessionId}`, { headers: getAuthHeaders() });
      setSelectedConversation(res.data);
    } catch (e) { console.error('Erreur chargement admin:', e); }
  };

  const loadLeads = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: getAuthHeaders() });
      setLeads(res.data || []);
    } catch (e) { console.error('Erreur chargement admin:', e); }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats?days=7`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (e) { console.error('Erreur chargement admin:', e); }
  };

  const loadAnalytics = async (days = analyticsDays) => {
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${days}`, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (e) { console.error('Erreur analytics:', e); }
  };

  const changeAnalyticsPeriod = (days) => {
    setAnalyticsDays(days);
    loadAnalytics(days);
  };

  const loadWeeklyPricing = async () => {
    try {
      const res = await axios.get(`${API}/admin/weekly-pricing`, { headers: getAuthHeaders() });
      setWeeklyPricing(res.data || []);
    } catch (e) { console.error('Erreur chargement admin:', e); }
  };

  const handleSaveWeeklyPrice = async (item) => {
    setSavingWeekId(item.id);
    try {
      await axios.put(`${API}/admin/weekly-pricing/${item.id}`, { price: parseInt(item.price, 10) || 0, electra: item.electra, booked: item.booked }, { headers: getAuthHeaders() });
      toast({ title: 'Tarif mis à jour', description: `Semaine du ${item.start} : ${item.price}€` });
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'enregistrer ce tarif.", variant: 'destructive' });
    } finally {
      setSavingWeekId(null);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const res = await axios.post(`${API}/admin/report/send`, {}, { headers: getAuthHeaders() });
      if (res.data.success) {
        toast({ title: 'Rapport envoyé', description: 'Le rapport hebdomadaire a été envoyé par email.' });
      } else {
        toast({ title: 'Rapport généré', description: `Email non envoyé (${res.data.error || 'SMTP'}). Il reste consultable ici.`, variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: "Impossible d'envoyer le rapport.", variant: 'destructive' });
    } finally {
      setSendingReport(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'conversations') { loadConversations(); loadLeads(); }
    if (activeTab === 'stats') { loadStats(); loadAnalytics(); }
    if (activeTab === 'pricing') loadWeeklyPricing();
  }, [activeTab]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-serif text-stone-800">Administration</h1>
            <p className="text-sm text-stone-600">Villa Nomade - Les Portes en Ré</p>
          </div>
          <div className="flex gap-4">
            <Link to="/">
              <Button variant="outline" className="border-amber-700 text-amber-700 hover:bg-amber-50">
                Voir le site
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="border-stone-300">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 border-b border-stone-200">
          <button
            onClick={() => setActiveTab('content')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'content'
                ? 'border-b-2 border-amber-700 text-amber-700'
                : 'text-stone-600 hover:text-amber-700'
            }`}
          >
            <FileText className="w-5 h-5 inline mr-2" />
            Contenu
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'pricing'
                ? 'border-b-2 border-amber-700 text-amber-700'
                : 'text-stone-600 hover:text-amber-700'
            }`}
          >
            <Euro className="w-5 h-5 inline mr-2" />
            Tarifs
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`pb-4 px-4 font-medium transition-colors relative ${
              activeTab === 'messages'
                ? 'border-b-2 border-amber-700 text-amber-700'
                : 'text-stone-600 hover:text-amber-700'
            }`}
          >
            <Mail className="w-5 h-5 inline mr-2" />
            Messages
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('conversations')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'conversations'
                ? 'border-b-2 border-amber-700 text-amber-700'
                : 'text-stone-600 hover:text-amber-700'
            }`}
          >
            <MessageCircle className="w-5 h-5 inline mr-2" />
            Chatbot
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`pb-4 px-4 font-medium transition-colors ${
              activeTab === 'stats'
                ? 'border-b-2 border-amber-700 text-amber-700'
                : 'text-stone-600 hover:text-amber-700'
            }`}
          >
            <BarChart3 className="w-5 h-5 inline mr-2" />
            Statistiques
          </button>
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-serif text-stone-800">
                Modifier le contenu de la page d'accueil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="availability-title" className="text-stone-700 text-base mb-2 block">
                  Titre de l'annonce de disponibilité
                </Label>
                <Input
                  id="availability-title"
                  value={availabilityContent.title}
                  onChange={(e) => setAvailabilityContent({ ...availabilityContent, title: e.target.value })}
                  placeholder="Ex: Disponibilités été 2026"
                />
              </div>
              
              <div>
                <Label htmlFor="availability-content" className="text-stone-700 text-base mb-2 block">
                  Message de disponibilité (affiché sur la page d'accueil)
                </Label>
                <Textarea
                  id="availability-content"
                  value={availabilityContent.content}
                  onChange={(e) => setAvailabilityContent({ ...availabilityContent, content: e.target.value })}
                  rows={4}
                  placeholder="Ex: Périodes encore disponibles pour l'été 2026 ! Dernières semaines de juillet et début septembre. Contactez-nous vite !"
                />
                <p className="text-sm text-stone-500 mt-2">
                  Ce message sera affiché en évidence sur la page d'accueil pour informer les visiteurs des disponibilités.
                </p>
              </div>

              <Button
                onClick={handleSaveContent}
                disabled={loading}
                className="bg-amber-700 hover:bg-amber-800 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
              </Button>

              <div className="border-t border-stone-200 pt-6 mt-2">
                <h3 className="text-lg font-serif text-stone-800 mb-1">Pavés de disponibilité (page Réservation)</h3>
                <p className="text-sm text-stone-500 mb-4">Modifiez librement ces deux encadrés — idéal pour vos annonces promotionnelles.</p>

                <div className="p-4 rounded-lg bg-green-50 border-2 border-green-200 mb-4">
                  <Label className="text-green-800 font-semibold mb-2 block">Pavé vert — Titre</Label>
                  <Input
                    value={availAvailableEdit.title}
                    onChange={(e) => setAvailAvailableEdit({ ...availAvailableEdit, title: e.target.value })}
                    placeholder="Ex: Encore disponible"
                  />
                  <Label className="text-green-800 font-semibold mt-3 mb-2 block">Pavé vert — Message</Label>
                  <Textarea
                    value={availAvailableEdit.content}
                    onChange={(e) => setAvailAvailableEdit({ ...availAvailableEdit, content: e.target.value })}
                    rows={4}
                    placeholder="Une ligne par point. Ex: • Semaine du 21 au 27 juin 2026"
                  />
                  <Button
                    onClick={() => handleSaveBlock('availability_available', availAvailableEdit)}
                    disabled={loading}
                    className="bg-green-700 hover:bg-green-800 text-white mt-3"
                  >
                    <Save className="w-4 h-4 mr-2" /> Enregistrer le pavé vert
                  </Button>
                </div>

                <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200">
                  <Label className="text-red-800 font-semibold mb-2 block">Pavé rouge — Titre</Label>
                  <Input
                    value={availBookedEdit.title}
                    onChange={(e) => setAvailBookedEdit({ ...availBookedEdit, title: e.target.value })}
                    placeholder="Ex: Déjà réservé (complet)"
                  />
                  <Label className="text-red-800 font-semibold mt-3 mb-2 block">Pavé rouge — Message</Label>
                  <Textarea
                    value={availBookedEdit.content}
                    onChange={(e) => setAvailBookedEdit({ ...availBookedEdit, content: e.target.value })}
                    rows={4}
                    placeholder="Une ligne par point. Ex: • Du 28 juin au 5 septembre 2026"
                  />
                  <Button
                    onClick={() => handleSaveBlock('availability_booked', availBookedEdit)}
                    disabled={loading}
                    className="bg-red-700 hover:bg-red-800 text-white mt-3"
                  >
                    <Save className="w-4 h-4 mr-2" /> Enregistrer le pavé rouge
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            {pricing.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-6">
                  {editingPricing?.id === item.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Période</Label>
                          <Input
                            value={editingPricing.period}
                            onChange={(e) => setEditingPricing({ ...editingPricing, period: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Prix</Label>
                          <Input
                            value={editingPricing.price}
                            onChange={(e) => setEditingPricing({ ...editingPricing, price: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={editingPricing.description}
                          onChange={(e) => setEditingPricing({ ...editingPricing, description: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSavePricing(editingPricing)}
                          className="bg-amber-700 hover:bg-amber-800 text-white"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Sauvegarder
                        </Button>
                        <Button
                          onClick={() => setEditingPricing(null)}
                          variant="outline"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-stone-800">{item.period}</h3>
                        <p className="text-2xl font-bold text-amber-700">{item.price}</p>
                        <p className="text-sm text-stone-600">{item.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setEditingPricing(item)}
                          variant="outline"
                          className="border-amber-700 text-amber-700"
                        >
                          Modifier
                        </Button>
                        <Button
                          onClick={() => handleDeletePricing(item.id)}
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Tarifs semaine par semaine */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-serif text-stone-800 flex items-center gap-2">
                  <Euro className="w-5 h-5 text-amber-700" /> Tarifs semaine par semaine ({weeklyPricing.length})
                </CardTitle>
                <p className="text-sm text-stone-500">Modifiez le loyer de chaque semaine. Cochez « élec. » si la consommation électrique est en sus.</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                  {weeklyPricing.map((item, idx) => (
                    <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg border border-stone-100 hover:bg-stone-50">
                      <div className="flex-1 text-sm text-stone-700">
                        <span className="font-medium">{item.start}</span>
                        <span className="text-stone-400"> → {item.end}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={item.price}
                          onChange={(e) => {
                            const v = e.target.value;
                            setWeeklyPricing((prev) => prev.map((p) => p.id === item.id ? { ...p, price: v } : p));
                          }}
                          className="w-24 h-9 text-right"
                        />
                        <span className="text-stone-500">€</span>
                      </div>
                      <label className="flex items-center gap-1 text-xs text-stone-600">
                        <input
                          type="checkbox"
                          checked={!!item.electra}
                          onChange={(e) => {
                            const c = e.target.checked;
                            setWeeklyPricing((prev) => prev.map((p) => p.id === item.id ? { ...p, electra: c } : p));
                          }}
                        />
                        élec.
                      </label>
                      <label className="flex items-center gap-1 text-xs text-red-600">
                        <input
                          type="checkbox"
                          checked={!!item.booked}
                          onChange={(e) => {
                            const c = e.target.checked;
                            setWeeklyPricing((prev) => prev.map((p) => p.id === item.id ? { ...p, booked: c } : p));
                          }}
                        />
                        occupé
                      </label>
                      <Button
                        size="sm"
                        onClick={() => handleSaveWeeklyPrice(item)}
                        disabled={savingWeekId === item.id}
                        className="bg-amber-700 hover:bg-amber-800 text-white h-9"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            {messages.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-stone-600">
                  Aucun message pour le moment
                </CardContent>
              </Card>
            ) : (
              messages.map((msg) => (
                <Card key={msg.id} className={msg.read ? 'bg-stone-50' : 'bg-white border-2 border-amber-200'}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-stone-800">{msg.name}</h3>
                        <p className="text-sm text-stone-600">{msg.email}</p>
                        {msg.phone && <p className="text-sm text-stone-600">{msg.phone}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-stone-500">
                          {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        {!msg.read && (
                          <span className="inline-block bg-amber-500 text-white text-xs px-2 py-1 rounded mt-1">
                            Nouveau
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {(msg.dates || msg.guests) && (
                      <div className="mb-3 flex gap-4 text-sm">
                        {msg.dates && <span className="text-stone-600">📅 {msg.dates}</span>}
                        {msg.guests && <span className="text-stone-600">👥 {msg.guests} personne(s)</span>}
                      </div>
                    )}
                    
                    <p className="text-stone-700 whitespace-pre-wrap mb-4">{msg.message}</p>
                    
                    <div className="flex gap-2">
                      <a href={`mailto:${msg.email}?subject=Re: Demande de réservation Villa Nomade`}>
                        <Button className="bg-amber-700 hover:bg-amber-800 text-white">
                          Répondre par email
                        </Button>
                      </a>
                      {!msg.read && (
                        <Button
                          onClick={() => handleMarkAsRead(msg.id)}
                          variant="outline"
                        >
                          Marquer comme lu
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDeleteMessage(msg.id)}
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Conversations Tab */}
        {activeTab === 'conversations' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-lg font-serif text-stone-800 flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-700" /> Emails collectés ({leads.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leads.length === 0 ? (
                    <p className="text-sm text-stone-500">Aucun email collecté pour le moment.</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {leads.map((l, i) => (
                        <div key={`${l.email}-${l.created_at}`} className="flex justify-between items-center text-sm border-b border-stone-100 pb-1">
                          <a href={`mailto:${l.email}`} className="text-amber-700 hover:underline">{l.email}</a>
                          <span className="text-xs text-stone-400">{(l.created_at || '').slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-serif text-stone-800">Conversations ({conversations.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {conversations.length === 0 ? (
                    <p className="text-sm text-stone-500">Aucune conversation pour le moment.</p>
                  ) : (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                      {conversations.map((c) => (
                        <button
                          key={c.session_id}
                          onClick={() => loadConversationDetail(c.session_id)}
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            selectedConversation?.session_id === c.session_id
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-stone-200 hover:bg-stone-50'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-stone-800">
                              {c.email ? c.email : 'Visiteur anonyme'}
                            </span>
                            <span className="text-xs text-stone-400">{c.message_count} msg</span>
                          </div>
                          <p className="text-xs text-stone-500 truncate mt-1">{c.last_message}</p>
                          <p className="text-[10px] text-stone-400 mt-1">{(c.last || '').replace('T', ' ').slice(0, 16)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-serif text-stone-800">Détail de la conversation</CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedConversation ? (
                  <p className="text-sm text-stone-500">Sélectionnez une conversation à gauche pour la lire.</p>
                ) : (
                  <div className="space-y-3 max-h-[560px] overflow-y-auto">
                    {selectedConversation.email && (
                      <div className="text-sm bg-green-50 border border-green-200 rounded p-2 text-green-800">
                        Email : <a href={`mailto:${selectedConversation.email}`} className="underline">{selectedConversation.email}</a>
                      </div>
                    )}
                    {selectedConversation.messages.map((m, i) => (
                      <div key={`${m.created_at}-${m.role}-${i}`} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                          m.role === 'user' ? 'bg-amber-700 text-white' : 'bg-stone-100 text-stone-800'
                        }`}>
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Statistiques Tab */}
        {activeTab === 'stats' && (
          <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-serif text-stone-800">Rapport de visite (7 derniers jours)</h2>
                {stats && <p className="text-sm text-stone-500">Période : {stats.period}</p>}
              </div>
              <Button onClick={handleSendReport} disabled={sendingReport} className="bg-amber-700 hover:bg-amber-800 text-white">
                <Send className="w-4 h-4 mr-2" />
                {sendingReport ? 'Envoi…' : 'Envoyer le rapport par email'}
              </Button>
            </div>
            {!stats ? (
              <p className="text-sm text-stone-500">Chargement des statistiques…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card><CardContent className="p-6 text-center">
                    <MessageCircle className="w-7 h-7 text-amber-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-stone-800">{stats.conversations}</div>
                    <div className="text-sm text-stone-500">Conversations</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-6 text-center">
                    <FileText className="w-7 h-7 text-amber-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-stone-800">{stats.messages}</div>
                    <div className="text-sm text-stone-500">Messages</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-6 text-center">
                    <Users className="w-7 h-7 text-green-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-stone-800">{stats.leads.length}</div>
                    <div className="text-sm text-stone-500">Emails collectés</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-6 text-center">
                    <Mail className="w-7 h-7 text-blue-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-stone-800">{stats.contacts}</div>
                    <div className="text-sm text-stone-500">Demandes contact</div>
                  </CardContent></Card>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle className="text-lg font-serif text-stone-800">Langues des visiteurs</CardTitle></CardHeader>
                    <CardContent>
                      {Object.keys(stats.languages || {}).length === 0 ? (
                        <p className="text-sm text-stone-500">Aucune donnée.</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(stats.languages).map(([lg, n]) => (
                            <div key={lg} className="flex justify-between text-sm border-b border-stone-100 pb-1">
                              <span className="font-medium text-stone-700">{lg}</span>
                              <span className="text-stone-500">{n} message(s)</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-lg font-serif text-stone-800">Emails collectés cette semaine</CardTitle></CardHeader>
                    <CardContent>
                      {stats.leads.length === 0 ? (
                        <p className="text-sm text-stone-500">Aucun email cette semaine.</p>
                      ) : (
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                          {stats.leads.map((l, i) => (
                            <div key={`${l.email}-${l.created_at}`} className="flex justify-between text-sm border-b border-stone-100 pb-1">
                              <a href={`mailto:${l.email}`} className="text-amber-700 hover:underline">{l.email}</a>
                              <span className="text-xs text-stone-400">{(l.created_at || '').slice(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
                <p className="text-xs text-stone-400 mt-6">
                  Un rapport complet est envoyé automatiquement par email chaque lundi à 8h.
                </p>

                {/* Analytics de visite : fréquentation, pages, IP */}
                {analytics && (
                  <div className="mt-10">
                    <div className="flex justify-between items-center flex-wrap gap-3 mb-1">
                      <h2 className="text-xl font-serif text-stone-800">Fréquentation du site</h2>
                      <div className="flex gap-1 bg-stone-100 rounded-lg p-1">
                        {[7, 30, 90].map((d) => (
                          <button
                            key={d}
                            onClick={() => changeAnalyticsPeriod(d)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                              analyticsDays === d ? 'bg-amber-700 text-white' : 'text-stone-600 hover:bg-stone-200'
                            }`}
                          >
                            {d} jours
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-stone-500 mb-4">Pages consultées et adresses IP des visiteurs, jour par jour.</p>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <Card><CardContent className="p-6 text-center">
                        <FileText className="w-7 h-7 text-amber-600 mx-auto mb-2" />
                        <div className="text-3xl font-bold text-stone-800">{analytics.total_views}</div>
                        <div className="text-sm text-stone-500">Pages vues</div>
                      </CardContent></Card>
                      <Card><CardContent className="p-6 text-center">
                        <Users className="w-7 h-7 text-blue-600 mx-auto mb-2" />
                        <div className="text-3xl font-bold text-stone-800">{analytics.unique_ips}</div>
                        <div className="text-sm text-stone-500">Visiteurs (IP uniques)</div>
                      </CardContent></Card>
                    </div>

                    {/* Graphique jour par jour */}
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg font-serif text-stone-800">Visites jour par jour</CardTitle>
                        <p className="text-sm text-stone-500">Repérez les journées où les visiteurs cherchent une location.</p>
                      </CardHeader>
                      <CardContent>
                        {analytics.days.length === 0 ? (
                          <p className="text-sm text-stone-500">Aucune donnée pour le moment.</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart
                              data={[...analytics.days].reverse().map((d) => ({
                                jour: d.date.slice(5),
                                Vues: d.views,
                                Visiteurs: d.unique_ips
                              }))}
                              margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                              <XAxis dataKey="jour" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="Vues" fill="#b45309" radius={[4, 4, 0, 0]} />
                              <Bar dataKey="Visiteurs" fill="#0891b2" radius={[4, 4, 0, 0]} />
                              <Line type="monotone" dataKey="Vues" name="Tendance (vues)" stroke="#78350f" strokeWidth={2} dot={false} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>

                    <div className="grid md:grid-cols-3 gap-6">
                      <Card>
                        <CardHeader><CardTitle className="text-lg font-serif text-stone-800">Par jour</CardTitle></CardHeader>
                        <CardContent>
                          {analytics.days.length === 0 ? (
                            <p className="text-sm text-stone-500">Aucune donnée.</p>
                          ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto">
                              <div className="flex justify-between text-xs text-stone-400 border-b pb-1">
                                <span>Date</span><span>Vues / IP</span>
                              </div>
                              {analytics.days.map((d) => (
                                <div key={d.date} className="flex justify-between text-sm border-b border-stone-100 pb-1">
                                  <span className="text-stone-700">{d.date}</span>
                                  <span className="text-stone-600">{d.views} vues · {d.unique_ips} IP</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader><CardTitle className="text-lg font-serif text-stone-800">Pages les plus vues</CardTitle></CardHeader>
                        <CardContent>
                          {analytics.top_pages.length === 0 ? (
                            <p className="text-sm text-stone-500">Aucune donnée.</p>
                          ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto">
                              {analytics.top_pages.map((p) => (
                                <div key={p.path} className="flex justify-between text-sm border-b border-stone-100 pb-1">
                                  <span className="text-stone-700 truncate mr-2">{p.path}</span>
                                  <span className="text-amber-700 font-medium whitespace-nowrap">{p.views}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader><CardTitle className="text-lg font-serif text-stone-800">Adresses IP ({analytics.unique_ips})</CardTitle></CardHeader>
                        <CardContent>
                          {analytics.ips.length === 0 ? (
                            <p className="text-sm text-stone-500">Aucune donnée.</p>
                          ) : (
                            <div className="space-y-1 max-h-72 overflow-y-auto">
                              {analytics.ips.map((v) => (
                                <div key={v.ip} className="flex justify-between text-sm border-b border-stone-100 pb-1">
                                  <span className="text-stone-700 font-mono text-xs">{v.ip}</span>
                                  <span className="text-stone-500 whitespace-nowrap">{v.count} vue(s)</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;